export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
export const MAX_PDF_PAGES = 25;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

export const validateFiles = (files: File[]) => {
  if (!files || files.length === 0) {
    throw new Error('No files selected for processing');
  }

  for (const file of files) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.name}. Only PDF, JPG, and PNG files are supported.`);
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(`File too large: ${file.name}. Maximum file size is 50MB.`);
    }
  }
};

/**
 * Returns the page count of a PDF, or null when it can't be parsed here
 * (the server enforces the same limit, so an unreadable file is not blocked).
 */
export async function countPdfPages(file: File): Promise<number | null> {
  try {
    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.load(await file.arrayBuffer(), {
      ignoreEncryption: true,
      updateMetadata: false,
    });
    return pdf.getPageCount();
  } catch {
    return null;
  }
}

export interface RejectedUpload {
  file: File;
  reason: string;
}

/**
 * Splits a selection into files we can process and files we refuse up front,
 * with a reason the user can act on.
 */
export async function checkUploads(files: File[]): Promise<{ accepted: File[]; rejected: RejectedUpload[] }> {
  const accepted: File[] = [];
  const rejected: RejectedUpload[] = [];

  for (const file of files) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      rejected.push({ file, reason: 'Only PDF, JPG and PNG files are supported.' });
      continue;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      rejected.push({ file, reason: 'The file is larger than 50MB.' });
      continue;
    }
    if (file.type === 'application/pdf') {
      const pages = await countPdfPages(file);
      if (pages !== null && pages > MAX_PDF_PAGES) {
        rejected.push({
          file,
          reason: `It has ${pages} pages; the limit is ${MAX_PDF_PAGES}. Split it into smaller PDFs and upload those.`,
        });
        continue;
      }
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}

export const validateOrder = (orderId: string | undefined) => {
  if (!orderId) {
    console.error('No active product order found');
    throw new Error('Please create or select an active order before uploading documents');
  }
};
